import { Component } from '@theme/component';

let groupIndex = 0;

/**
 * Kaching-Bundles style quantity tier selector.
 *
 * Each tier is a radio input carrying its own pricing rules as data attributes.
 * Selecting a tier writes its quantity into the product form's quantity input,
 * so the theme's own add-to-cart flow stays untouched.
 */
class QuantityTierSelectorComponent extends Component {
  #groupName = '';
  #variants = new Map();
  #syncing = false;
  #observer = null;
  #fallbackQuantityInput = null;
  #boundFormChange = null;

  connectedCallback() {
    super.connectedCallback?.();

    this.#groupName = `quantity-tier-group-${++groupIndex}`;
    for (const input of this.tierInputs) input.name = this.#groupName;

    this.#readVariants();
    this.#ensureSelection();
    this.#syncVariantFromForm();
    this.render();
    this.#pushQuantityToForm();

    this.#boundFormChange = () => this.#onFormChange();
    this.productForm?.addEventListener('change', this.#boundFormChange);

    // The variant id input is often updated as a property, which `change` does
    // not always cover, so watch attribute writes too.
    if (this.productForm) {
      this.#observer = new MutationObserver(() => this.#onFormChange());
      this.#observer.observe(this.productForm, {
        attributes: true,
        attributeFilter: ['value'],
        subtree: true,
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#observer?.disconnect();
    if (this.#boundFormChange) this.productForm?.removeEventListener('change', this.#boundFormChange);
  }

  /* ------------------------------------------------------------------ refs */

  /** @returns {HTMLInputElement[]} */
  get tierInputs() {
    const refs = this.refs?.tierInputs;
    if (Array.isArray(refs) && refs.length) return refs;
    return Array.from(this.querySelectorAll('.quantity-tier__radio-input'));
  }

  get selectedInput() {
    return this.tierInputs.find((input) => input.checked) ?? this.tierInputs[0] ?? null;
  }

  get productForm() {
    return (
      this.closest('form[action*="/cart/add"]') ??
      this.closest('.shopify-section, product-form-component, .product-information')?.querySelector('form[action*="/cart/add"]') ??
      document.querySelector('form[action*="/cart/add"]')
    );
  }

  get quantityInput() {
    const form = this.productForm;
    if (!form) return null;

    const existing = form.querySelector('input[name="quantity"], select[name="quantity"]');
    if (existing) return existing;

    // Switching the theme's quantity block off in the editor drops
    // input[name="quantity"] from the form, and every add-to-cart would then
    // silently fall back to a single item. Carry the tier quantity ourselves.
    // The theme submits via new FormData(form), so a hidden input is enough.
    if (!this.#fallbackQuantityInput?.isConnected) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'quantity';
      input.value = '1';
      form.append(input);
      this.#fallbackQuantityInput = input;
    }
    return this.#fallbackQuantityInput;
  }

  get variantIdInput() {
    return this.productForm?.querySelector('input[name="id"], select[name="id"]') ?? null;
  }

  /* --------------------------------------------------------------- handlers */

  /** Bound through `on:change="/handleTierChange"`. */
  handleTierChange(event) {
    const input = event.target.closest?.('.quantity-tier__radio-input') ?? event.target;
    if (!(input instanceof HTMLInputElement)) return;

    for (const other of this.tierInputs) other.checked = other === input;

    this.render();
    this.#pushQuantityToForm();
    this.#applyDiscountCode();
    this.#emitChange(input);
  }

  /** Bound through `on:click="/stepQuantity"`. */
  stepQuantity(event) {
    const button = event.target.closest('[data-step]');
    if (!button) return;

    event.preventDefault();

    const card = button.closest('.quantity-tier__card');
    const input = card?.querySelector('.quantity-tier__radio-input');
    if (!input) return;

    const step = Number(button.dataset.step) || 0;
    const min = Number(input.dataset.quantity) || 1;
    const max = Number(input.dataset.maxQuantity) || 99;
    const next = Math.min(max, Math.max(min, this.#quantityOf(input) + step));

    input.dataset.currentQuantity = String(next);

    if (!input.checked) {
      for (const other of this.tierInputs) other.checked = other === input;
    }

    this.render();
    this.#pushQuantityToForm();
    this.#emitChange(input);
  }

  #onFormChange() {
    if (this.#syncing) return;
    if (this.#syncVariantFromForm()) {
      this.render();
      return;
    }
    this.#adoptExternalQuantity();
  }

  /* ------------------------------------------------------------------ state */

  #readVariants() {
    const json = this.querySelector('[data-quantity-tier-variants]')?.textContent;
    if (!json) return;
    try {
      for (const variant of JSON.parse(json)) this.#variants.set(String(variant.id), variant);
    } catch {
      /* ignore malformed variant data */
    }
  }

  /** @returns {boolean} true when the variant actually changed */
  #syncVariantFromForm() {
    const id = this.variantIdInput?.value;
    if (!id || id === this.dataset.variantId) return false;

    const variant = this.#variants.get(String(id));
    this.dataset.variantId = id;
    if (variant) {
      this.dataset.variantPrice = String(variant.price ?? 0);
      this.dataset.variantComparePrice = String(variant.compare_at_price ?? 0);
    }
    return true;
  }

  #ensureSelection() {
    const inputs = this.tierInputs;
    if (!inputs.length) return;
    if (!inputs.some((input) => input.checked)) inputs[0].checked = true;
  }

  #quantityOf(input) {
    return Number(input.dataset.currentQuantity || input.dataset.quantity) || 1;
  }

  #pushQuantityToForm() {
    const input = this.selectedInput;
    const quantityInput = this.quantityInput;
    if (!input || !quantityInput) return;

    const quantity = String(this.#quantityOf(input));
    if (quantityInput.value === quantity) return;

    this.#syncing = true;
    quantityInput.value = quantity;
    quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
    quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    this.#syncing = false;
  }

  /** Keep the tiers honest when the shopper uses the theme's own quantity widget. */
  #adoptExternalQuantity() {
    const quantityInput = this.quantityInput;
    const selected = this.selectedInput;
    if (!quantityInput || !selected) return;

    const value = Number(quantityInput.value);
    if (!value || value === this.#quantityOf(selected)) return;

    const exact = this.tierInputs.find((input) => Number(input.dataset.quantity) === value);
    const stepper = this.tierInputs.find(
      (input) =>
        input.closest('.quantity-tier__card')?.querySelector('.quantity-tier__stepper') &&
        value >= (Number(input.dataset.quantity) || 1) &&
        value <= (Number(input.dataset.maxQuantity) || 99)
    );

    const target = exact ?? stepper;
    if (!target) return;

    for (const other of this.tierInputs) other.checked = other === target;
    target.dataset.currentQuantity = String(value);
    this.render();
  }

  #applyDiscountCode() {
    const code = this.dataset.discountCode;
    if (!code) return;
    const discount = Number(this.selectedInput?.dataset.discountPercentage) || 0;
    if (discount <= 0) return;
    // Sets Shopify's discount cookie so the code is live at checkout.
    fetch(`${Shopify?.routes?.root ?? '/'}discount/${encodeURIComponent(code)}`, {
      method: 'GET',
      credentials: 'same-origin',
    }).catch(() => {});
  }

  #emitChange(input) {
    this.dispatchEvent(
      new CustomEvent('quantity-tier:change', {
        bubbles: true,
        detail: {
          quantity: this.#quantityOf(input),
          discountPercentage: Number(input.dataset.discountPercentage) || 0,
          variantId: this.dataset.variantId,
        },
      })
    );
  }

  /* ----------------------------------------------------------------- render */

  render() {
    const unitPrice = Number(this.dataset.variantPrice) || 0;
    const unitCompare = Number(this.dataset.variantComparePrice) || 0;
    const onSale = unitCompare > unitPrice;

    for (const input of this.tierInputs) {
      const card = input.closest('.quantity-tier__card');
      if (!card) continue;

      const quantity = this.#quantityOf(input);
      const discount = Number(input.dataset.discountPercentage) || 0;
      const hideCents = input.dataset.hideCents === 'true';

      const baseTotal = unitPrice * quantity;
      const saleTotal = Math.floor((baseTotal * (100 - discount)) / 100);
      const compareTotal = (onSale ? unitCompare : unitPrice) * quantity;

      card.classList.toggle('quantity-tier__card--selected', input.checked);

      const saleEl = card.querySelector('.quantity-tier__price-sale');
      if (saleEl) saleEl.textContent = this.formatMoney(saleTotal, hideCents);

      const compareEl = card.querySelector('.quantity-tier__price-compare');
      if (compareEl) {
        compareEl.textContent = this.formatMoney(compareTotal, hideCents);
        compareEl.hidden = input.dataset.showCompare !== 'true' || compareTotal <= saleTotal;
        compareEl.classList.toggle('quantity-tier__price-compare--sale', onSale);
        compareEl.classList.toggle('quantity-tier__price-compare--plain', input.dataset.showStrikethrough !== 'true');
      }

      this.#renderBadge(card, input, { onSale, saleTotal, compareTotal, discount });
      this.#renderStepper(card, input, quantity);
    }

    this.#renderProductPrice({ unitPrice, unitCompare, onSale });
  }

  /**
   * Mirror the selected tier's total into the theme's own price block, so the
   * headline price tracks the tier the shopper picked.
   */
  #renderProductPrice({ unitPrice, unitCompare, onSale }) {
    if (this.dataset.updateProductPrice !== 'true') return;

    const container = this.productPriceContainer;
    const input = this.selectedInput;
    if (!container || !input) return;

    const quantity = this.#quantityOf(input);
    const discount = Number(input.dataset.discountPercentage) || 0;
    const hideCents = input.dataset.hideCents === 'true';

    const total = Math.floor((unitPrice * quantity * (100 - discount)) / 100);
    const compareTotal = (onSale ? unitCompare : unitPrice) * quantity;

    const money = this.formatMoney(total, hideCents);
    for (const node of container.querySelectorAll('.price__regular .price, .price__sale .price--sale')) {
      node.textContent = money;
    }

    const compareNode = container.querySelector('.compare-at-price');
    if (compareNode) {
      const showCompare = compareTotal > total;
      compareNode.textContent = showCompare ? this.formatMoney(compareTotal, hideCents) : '';
    }

    // What the same quantity would cost without the tier discount, struck
    // through ahead of the new price.
    const undiscounted = unitPrice * quantity;
    this.#renderHeadlineCompare(container, undiscounted > total ? this.formatMoney(undiscounted, hideCents) : '');
  }

  #renderHeadlineCompare(container, text) {
    const color = getComputedStyle(this).getPropertyValue('--quantity-tier-compare-price-color').trim();

    for (const wrapper of container.querySelectorAll('.price__regular, .price__sale')) {
      const price = wrapper.querySelector('.price');
      if (!price) continue;

      let node = wrapper.querySelector('.quantity-tier__headline-compare');

      if (!text) {
        node?.remove();
        continue;
      }

      if (!node) {
        node = document.createElement('s');
        node.className = 'quantity-tier__headline-compare';
        price.before(node);
      }

      node.textContent = text;
      if (color) node.style.color = color;
    }
  }

  get productPriceContainer() {
    const scope = this.closest('.shopify-section') ?? document;
    return scope.querySelector('product-price [ref="priceContainer"]') ?? scope.querySelector('product-price');
  }

  #renderBadge(card, input, { onSale, saleTotal, compareTotal, discount }) {
    const holder = card.querySelector('.quantity-tier__badges');
    if (!holder) return;

    const showSale = input.dataset.showSale === 'true' && onSale && compareTotal > saleTotal;
    const showDiscount = input.dataset.showBadge === 'true' && discount > 0;

    let text = '';
    let modifier = '';

    if (showSale) {
      const saved = Math.floor(((compareTotal - saleTotal) * 100) / compareTotal);
      text = (input.dataset.saleBadgeText || 'SAVE [percent]%').replace('[percent]', String(saved));
      modifier = ' quantity-tier__badge--sale';
    } else if (showDiscount) {
      text = (input.dataset.badgeText || '[percent]% korting').replace('[percent]', String(discount));
    }

    holder.innerHTML = text ? `<span class="quantity-tier__badge${modifier}">${text}</span>` : '';
  }

  #renderStepper(card, input, quantity) {
    const stepper = card.querySelector('.quantity-tier__stepper');
    if (!stepper) return;

    const min = Number(input.dataset.quantity) || 1;
    const max = Number(input.dataset.maxQuantity) || 99;

    const value = stepper.querySelector('.quantity-tier__stepper-value');
    if (value) value.textContent = String(quantity);

    const minus = stepper.querySelector('[data-step="-1"]');
    const plus = stepper.querySelector('[data-step="1"]');
    if (minus) minus.disabled = quantity <= min;
    if (plus) plus.disabled = quantity >= max;
  }

  /* ------------------------------------------------------------ money utils */

  formatMoney(cents, hideCents = false) {
    // A space between the symbol and the amount, matching the Liquid render.
    const format = (this.dataset.moneyFormat || '${{amount}}').replace(/(\S)\{\{/, '$1 {{');
    const value = hideCents ? Math.floor(cents / 100) * 100 : cents;

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, name) => {
      const noDecimals = hideCents || name.includes('no_decimals');
      const amount = (value / 100).toFixed(noDecimals ? 0 : 2);
      const [whole, fraction] = amount.split('.');

      if (name.includes('comma_separator')) {
        return group(whole, '.') + (fraction ? `,${fraction}` : '');
      }
      if (name.includes('apostrophe_separator')) {
        return group(whole, "'") + (fraction ? `.${fraction}` : '');
      }
      if (name.includes('space_separator')) {
        return group(whole, ' ') + (fraction ? `,${fraction}` : '');
      }
      return group(whole, ',') + (fraction ? `.${fraction}` : '');
    });
  }
}

function group(whole, separator) {
  return whole.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
}

if (!customElements.get('quantity-tier-selector-component')) {
  customElements.define('quantity-tier-selector-component', QuantityTierSelectorComponent);
}
